import React, { useRef } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { ChevronDownIcon, XIcon } from './Icons';

const Select = React.forwardRef(({ 
  className, 
  /** Applied to the native select element (e.g. rounded-xl). Width/max-width belong in `className` (wrapper). */
  selectClassName,
  children,
  error,
  placeholder,
  selected = false,
  value,
  onChange,
  name,
  disabled,
  readOnly,
  showClearButton = true,
  prefix,
  suffix,
  showCaret = true,
  ...props 
}, ref) => {
  const { theme } = useTheme();
  const innerRef = useRef(null);
  
  const baseClasses = `
    flex h-10 w-full rounded-md border px-3 py-2 text-sm
    transition-all duration-200 ease-in-out
    focus:outline-none focus:ring-2 focus:ring-blue-500/20
    disabled:cursor-not-allowed disabled:opacity-50
    backdrop-blur-sm hover:shadow-sm
    appearance-none cursor-pointer
  `;
  
  // Different styling based on selection state
  const getThemeClasses = () => {
    if (error) {
      return `${theme.colors.surface} ${theme.text.primary} border-red-500 focus:border-red-500 focus:ring-red-500/20`;
    }
    
    if (selected) {
      // Selected state - modern gradient and enhanced styling
      return `bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 border-blue-300 dark:border-blue-600 ${theme.text.primary} focus:border-blue-500 focus:ring-blue-500/20 shadow-sm`;
    }
    
    // Default state
    return `${theme.colors.surface} ${theme.text.primary} ${theme.border.light} hover:${theme.border.default} focus:${theme.border.focus}`;
  };
  
  const hasValue = value !== undefined && value !== null && value !== '';
  const isDisabled = disabled || readOnly;
  const hasRightAdornment = Boolean(suffix) || Boolean(showCaret);
  
  // Dynamic padding to accommodate suffix and clear button
  const needsClearPad = showClearButton && hasValue && !isDisabled;
  const rightPadClass = needsClearPad && hasRightAdornment
    ? 'pr-24'
    : needsClearPad
    ? 'pr-20'
    : hasRightAdornment
    ? 'pr-14'
    : '';
  const leftPadClass = prefix ? 'pl-10' : '';
  
  // Layout classes (max-w-*, w-*, etc.) must live on the relative wrapper so the
  // absolute caret/clear button align with the visible field, not the full row width.
  const selectClasses = cn(
    baseClasses,
    getThemeClasses(),
    // subtle highlight when a value is selected
    hasValue ? 'border-blue-300 bg-blue-50 dark:bg-blue-950/20' : '',
    rightPadClass,
    leftPadClass,
    selectClassName,
  );
  
  const handleClear = (event) => {
    event.stopPropagation();
    if (onChange) {
      onChange({
        target: {
          name,
          value: '',
          type: 'select-one'
        }
      });
    }
    const el = (ref && 'current' in (ref || {})) ? ref.current : innerRef.current;
    if (el) {
      try { el.focus(); } catch (_) {}
    }
  };
  
  return (
    <div
      className={cn(
        'relative group w-full',
        className,
        !isDisabled && 'cursor-pointer',
      )}
    >
      {/* Prefix icon/content */}
      {prefix && (
        <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-500 z-10">
          {prefix}
        </div>
      )}
      
      <select
        className={selectClasses}
        ref={ref || innerRef}
        value={value}
        onChange={onChange}
        name={name}
        disabled={isDisabled}
        {...props}
      >
        {placeholder && (
          <option value="" disabled>
            {placeholder}
          </option>
        )}
        {children}
      </select>
      
      {/* Suffix icon/content (placed before clear button space) */}
      {suffix && (
        <div className="absolute right-12 top-1/2 -translate-y-1/2 pointer-events-none text-gray-500 z-10">
          {suffix}
        </div>
      )}

      {/* Default caret when no custom suffix is provided */}
      {!suffix && showCaret && (
        <div
          className={cn(
            "absolute top-1/2 -translate-y-1/2 text-gray-500 z-10",
            needsClearPad ? 'right-12' : 'right-3.5'
          )}
          aria-hidden="true"
        >
          <ChevronDownIcon className="w-4 h-4 transition-transform duration-200" />
        </div>
      )}

      {showClearButton && hasValue && !isDisabled && (
        <button
          type="button"
          onClick={handleClear}
          className={cn(
            "absolute right-2.5 top-1/2 -translate-y-1/2 inline-flex items-center justify-center h-8 w-8",
            "rounded-md border backdrop-blur-sm",
            "bg-white/80 dark:bg-gray-900/50 border-gray-200 dark:border-gray-700 shadow-sm",
            "text-gray-500 hover:text-red-600 dark:text-gray-300 dark:hover:text-red-400",
            "hover:bg-red-50 dark:hover:bg-red-950/20 hover:border-red-200 dark:hover:border-red-800",
            "focus:outline-none focus:ring-2 focus:ring-red-500/30",
            "transition-all duration-200"
          )}
          title="Clear selection"
          aria-label="Clear selection"
          tabIndex={-1}
        >
          <XIcon className="w-4 h-4" />
        </button>
      )}
    </div>
  );
});

const SelectItem = React.forwardRef(({ 
  className, 
  children,
  value,
  ...props 
}, ref) => {
  return (
    <option
      ref={ref}
      value={value}
      className={cn("", className)}
      {...props}
    >
      {children}
    </option>
  );
});

Select.displayName = "Select";
SelectItem.displayName = "SelectItem";

export { Select, SelectItem };
